import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bold,
  BrainCircuit,
  ClipboardCheck,
  Clock3,
  FilePlus2,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  MessageSquareText,
  Mic,
  Pill,
  Pin,
  Save,
  Search,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Underline,
} from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import type { PatientTreatmentPlanListResponse } from '../lib/treatmentPlans';

type PatientNote = {
  id: string;
  patientId: string;
  treatmentPlanId: string | null;
  title: string;
  contentHtml: string;
  contentText: string;
  pinned: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    firstName: string;
    lastName: string;
    title: string | null;
  };
  treatmentPlan: {
    id: string;
    title: string;
  } | null;
};

type NoteAiAutocompleteResponse = {
  completionText: string;
  completionHtml: string;
};

type NoteCopilotResponse = {
  suggestions: {
    differential: Array<{
      label: string;
      rank: number;
      why: string[];
    }>;
    missing_data_prompts: Array<{
      category: 'HPI' | 'ROS' | 'PE' | 'PMH' | 'Meds' | 'Allergies' | 'Social' | 'Family' | 'Diagnostics';
      prompt: string;
    }>;
    red_flags: Array<{
      title: string;
      severity: 'low' | 'medium' | 'high';
      recommended_action: string;
    }>;
  };
  entities_to_store: {
    orders_recommended: Array<{
      type: 'lab' | 'imaging' | 'medication' | 'referral' | 'other';
      text: string;
      rationale: string;
    }>;
  };
};

type EchoAiResponse = {
  title: string;
  contentText: string;
  contentHtml: string;
  checklist: string[];
  mode: 'model' | 'deterministic-fallback';
};

type LiveSuggestionSectionKey = 'redFlags' | 'differential' | 'additionalQuestions' | 'nextDocumentationLine';

type LiveSuggestionChip = {
  text: string;
  key: string;
};

type LiveSuggestionSection = {
  section: LiveSuggestionSectionKey;
  chips: LiveSuggestionChip[];
};

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function htmlToText(html: string) {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return (div.textContent || div.innerText || '').trim();
}

function buildSoapTemplate(tr: (value: string) => string) {
  return `<h3>${tr('Subjective')}</h3><p></p><h3>${tr('Objective')}</h3><p></p><h3>${tr('Assessment')}</h3><p></p><h3>${tr('Plan')}</h3><p></p>`;
}

function escapeHtml(value: string) {
  return String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function plainTextToHtml(text: string) {
  const escaped = escapeHtml(text);
  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

function plainTextToInlineHtml(text: string) {
  return escapeHtml(text).replace(/\n+/g, ' ');
}

function normalizeSuggestionKey(text: string) {
  return String(text || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function splitSuggestionIntoChips(text: string) {
  const normalized = String(text || '').trim();
  if (!normalized) return [];

  const parts = normalized
    .split(/\n+|(?:^|\s)[•-]\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  const unique = new Set<string>();
  return parts.filter((part) => {
    const key = normalizeSuggestionKey(part);
    if (!key || unique.has(key)) return false;
    unique.add(key);
    return true;
  });
}

function mapSuggestionHeadingToSection(heading: string): LiveSuggestionSectionKey {
  const normalized = normalizeSuggestionKey(heading).replace(/:$/, '');
  if (normalized.includes('red flag')) return 'redFlags';
  if (normalized.includes('differential')) return 'differential';
  if (normalized.includes('missing question') || normalized.includes('additional question')) return 'additionalQuestions';
  return 'nextDocumentationLine';
}

function parseLiveSuggestionSections(text: string): LiveSuggestionSection[] {
  const normalized = String(text || '').trim();
  if (!normalized) return [];

  const lines = normalized.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return [];

  const sections: LiveSuggestionSection[] = [];
  let current: LiveSuggestionSection | null = null;

  const pushCurrent = () => {
    if (!current) return;
    const seen = new Set<string>();
    current.chips = current.chips.filter((chip) => {
      if (!chip.key || seen.has(chip.key)) return false;
      seen.add(chip.key);
      return true;
    });
    if (current.chips.length) sections.push(current);
  };

  for (const line of lines) {
    const headingMatch = line.match(/^([A-Za-z][A-Za-z\s]+):\s*(.*)$/);
    if (headingMatch) {
      const [, heading, rest] = headingMatch;
      const nextSection: LiveSuggestionSection = {
        section: mapSuggestionHeadingToSection(heading),
        chips: [],
      };
      pushCurrent();
      current = nextSection;
      if (rest.trim()) {
        current.chips = splitSuggestionIntoChips(rest).map((item) => ({ text: item, key: normalizeSuggestionKey(item) }));
      }
      continue;
    }

    if (!current) {
      current = { section: 'nextDocumentationLine', chips: [] };
    }

    const chipText = line.replace(/^[•-]\s*/, '').trim();
    if (chipText) current.chips.push({ text: chipText, key: normalizeSuggestionKey(chipText) });
  }

  pushCurrent();

  if (!sections.length) {
    return [
      {
        section: 'nextDocumentationLine',
        chips: splitSuggestionIntoChips(normalized).map((item) => ({ text: item, key: normalizeSuggestionKey(item) })),
      },
    ];
  }

  return sections;
}

const BIDI_CONTROL_CHARS_RE = /[\u202A-\u202E\u2066-\u2069]/;
const BIDI_CONTROL_CHARS_GLOBAL_RE = /[\u202A-\u202E\u2066-\u2069]/g;
const RTL_OR_BIDI_MARKUP_RE = /(<\s*bdo\b|dir\s*=\s*["']?\s*rtl\b|direction\s*:\s*rtl\b|unicode-bidi\s*:)/i;

function sanitizeDirectionalMarkup(html: string) {
  const source = String(html || '<p></p>');
  if (!RTL_OR_BIDI_MARKUP_RE.test(source) && !BIDI_CONTROL_CHARS_RE.test(source)) {
    return source;
  }

  const container = document.createElement('div');
  container.innerHTML = source.replace(BIDI_CONTROL_CHARS_GLOBAL_RE, '');

  container.querySelectorAll('bdo').forEach((node) => {
    const replacement = document.createElement('span');
    replacement.innerHTML = node.innerHTML;
    node.replaceWith(replacement);
  });

  container.querySelectorAll<HTMLElement>('*').forEach((el) => {
    const dir = (el.getAttribute('dir') || '').trim().toLowerCase();
    if (dir === 'rtl') {
      el.removeAttribute('dir');
    }

    const style = el.getAttribute('style');
    if (!style) return;

    const cleanedStyle = style
      .replace(/(?:^|;)\s*direction\s*:\s*rtl\s*(?=;|$)/gi, '')
      .replace(/(?:^|;)\s*unicode-bidi\s*:\s*[^;]*/gi, '')
      .replace(/;;+/g, ';')
      .replace(/^\s*;\s*|\s*;\s*$/g, '')
      .trim();

    if (cleanedStyle) {
      el.setAttribute('style', cleanedStyle);
    } else {
      el.removeAttribute('style');
    }
  });

  return container.innerHTML || '<p></p>';
}

export function PatientNotesModule({ patientId, preferredNoteId }: { patientId: string; preferredNoteId?: string | null }) {
  const { fetchJson, fetchApi } = useApiClient();
  const { tr, language } = useI18n();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const isCreatingRef = useRef(false);
  const autoSuggestTimerRef = useRef<number | null>(null);
  const autoSuggestRequestIdRef = useRef(0);
  const suggestedKeysRef = useRef<Set<string>>(new Set());
  const lastAutocompleteInputKeyRef = useRef('');

  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [plans, setPlans] = useState<Array<{ id: string; title: string; status: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftTreatmentPlanId, setDraftTreatmentPlanId] = useState('');
  const [draftHtml, setDraftHtml] = useState('<p></p>');
  const [draftPinned, setDraftPinned] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [autoSuggestEnabled, setAutoSuggestEnabled] = useState(true);
  const [liveSuggestionSections, setLiveSuggestionSections] = useState<LiveSuggestionSection[]>([]);
  const [liveSuggestLoading, setLiveSuggestLoading] = useState(false);
  const [liveSuggestError, setLiveSuggestError] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState<'analysis' | 'treatment' | 'summary' | 'prescription' | 'voice' | 'conversation' | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedNote = useMemo(() => notes.find((note) => note.id === selectedId) || null, [notes, selectedId]);
  const copilotLanguage: 'en' | 'fr' | 'es' | 'it' = language === 'es' ? 'es' : language === 'it' ? 'it' : 'en';
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    isCreatingRef.current = isCreating;
  }, [isCreating]);

  useEffect(() => {
    return () => {
      if (autoSuggestTimerRef.current) {
        window.clearTimeout(autoSuggestTimerRef.current);
      }
    };
  }, []);

  const applyDraftFromNote = useCallback((note: PatientNote | null) => {
    if (!note) {
      selectedIdRef.current = null;
      isCreatingRef.current = true;
      setSelectedId(null);
      setIsCreating(true);
      setDraftTitle('');
      setDraftTreatmentPlanId('');
      setDraftHtml('<p></p>');
      setDraftPinned(false);
      setDirty(false);
      setLiveSuggestionSections([]);
      setLiveSuggestError(null);
      suggestedKeysRef.current = new Set();
      lastAutocompleteInputKeyRef.current = '';
      return;
    }
    selectedIdRef.current = note.id;
    isCreatingRef.current = false;
    setSelectedId(note.id);
    setIsCreating(false);
    setDraftTitle(note.title);
    setDraftTreatmentPlanId(note.treatmentPlanId || '');
    setDraftHtml(sanitizeDirectionalMarkup(note.contentHtml || '<p></p>'));
    setDraftPinned(note.pinned);
    setDirty(false);
    setLiveSuggestionSections([]);
    setLiveSuggestError(null);
    suggestedKeysRef.current = new Set();
    lastAutocompleteInputKeyRef.current = '';
  }, []);

  const loadNotes = useCallback(
    async (preferredNoteId?: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.set('q', search.trim());
        if (pinnedOnly) params.set('pinned', 'true');
        const query = params.toString();
        const data = await fetchJson<PatientNote[]>(`/patients/${patientId}/notes${query ? `?${query}` : ''}`);
        setNotes(data);
        if (preferredNoteId !== undefined) {
          const preferred = preferredNoteId ? data.find((note) => note.id === preferredNoteId) || null : null;
          applyDraftFromNote(preferred);
          return;
        }
        if (isCreatingRef.current) {
          return;
        }
        const nextId = selectedIdRef.current;
        const target = nextId ? data.find((note) => note.id === nextId) || null : data[0] || null;
        applyDraftFromNote(target);
      } catch (err: any) {
        setError(err.message || tr('Unable to load notes.'));
      } finally {
        setLoading(false);
      }
    },
    [applyDraftFromNote, fetchJson, patientId, pinnedOnly, search, tr],
  );

  useEffect(() => {
    loadNotes(preferredNoteId ?? undefined);
  }, [loadNotes, preferredNoteId]);

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      try {
        const data = await fetchJson<PatientTreatmentPlanListResponse>(`/treatment-plans/patients/${patientId}/plans?limit=100`);
        if (cancelled) return;
        const rows = Array.isArray(data.data) ? data.data : [];
        setPlans(rows.map((plan) => ({ id: plan.id, title: plan.title, status: plan.status })));
      } catch {
        if (!cancelled) setPlans([]);
      }
    }

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [fetchJson, patientId]);

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = sanitizeDirectionalMarkup(draftHtml || '<p></p>');
  }, [selectedId, isCreating]);

  function safelySwitch(action: () => void) {
    if (dirty && !window.confirm(tr('You have unsaved note changes. Continue without saving?'))) return;
    action();
  }

  function createNewNote() {
    safelySwitch(() => {
      applyDraftFromNote(null);
      setBanner(null);
      setError(null);
    });
  }

  function selectNote(note: PatientNote) {
    safelySwitch(() => {
      applyDraftFromNote(note);
      setBanner(null);
      setError(null);
    });
  }

  function runCommand(command: string, value?: string) {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    setDraftHtml(sanitizeDirectionalMarkup(editorRef.current.innerHTML || '<p></p>'));
    setDirty(true);
  }

  function insertHtmlAtCursor(html: string) {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const inserted = document.execCommand('insertHTML', false, html);
    if (!inserted) {
      editorRef.current.innerHTML = `${editorRef.current.innerHTML}${html}`;
    }
    setDraftHtml(sanitizeDirectionalMarkup(editorRef.current.innerHTML || '<p></p>'));
    setDirty(true);
  }

  async function insertImageFromFile(file: File) {
    const maxSizeBytes = 3 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(tr('Image is too large. Maximum size is 3 MB per image.'));
      return;
    }

    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(tr('Failed to read image file.')));
      reader.readAsDataURL(file);
    });

    const safeName = file.name.replace(/"/g, '');
    insertHtmlAtCursor(`<figure><img src="${dataUrl}" alt="${safeName}" style="max-width:100%;height:auto;" /><figcaption>${safeName}</figcaption></figure><p></p>`);
    setBanner(tr('Image inserted into note.'));
    setError(null);
  }

  function insertLiveSuggestionChip(chip: string) {
    if (!chip.trim()) return;
    const key = normalizeSuggestionKey(chip);
    if (key) {
      suggestedKeysRef.current.add(key);
    }
    insertHtmlAtCursor(`${editorRef.current?.textContent?.match(/\S$/) ? ' ' : ''}${plainTextToInlineHtml(chip)}`);
    setLiveSuggestionSections((current) =>
      current
        .map((section) => ({
          ...section,
          chips: section.chips.filter((chipItem) => chipItem.key !== key),
        }))
        .filter((section) => section.chips.length),
    );
  }

  function scheduleLiveAutocomplete(currentHtml: string) {
    if (!autoSuggestEnabled) {
      setLiveSuggestionSections([]);
      setLiveSuggestLoading(false);
      setLiveSuggestError(null);
      return;
    }

    const currentText = htmlToText(currentHtml);
    if (currentText.length < 8) {
      setLiveSuggestionSections([]);
      setLiveSuggestLoading(false);
      setLiveSuggestError(null);
      return;
    }

    const currentTextKey = normalizeSuggestionKey(currentText);
    if (currentTextKey === lastAutocompleteInputKeyRef.current) {
      return;
    }

    if (autoSuggestTimerRef.current) {
      window.clearTimeout(autoSuggestTimerRef.current);
    }

    autoSuggestTimerRef.current = window.setTimeout(async () => {
      const requestId = ++autoSuggestRequestIdRef.current;
      lastAutocompleteInputKeyRef.current = currentTextKey;
      setLiveSuggestLoading(true);
      setLiveSuggestError(null);
      try {
        const ai = await fetchJson<NoteAiAutocompleteResponse>(`/patients/${patientId}/notes/ai/autocomplete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: draftTitle,
            contentHtml: currentHtml,
            contentText: currentText,
            language: copilotLanguage,
          }),
        });
        if (requestId !== autoSuggestRequestIdRef.current) return;
        const rawSuggestion = String(ai.completionText || '').trim();
        let suggestion = rawSuggestion;
        if (rawSuggestion.toLowerCase().startsWith(currentText.toLowerCase())) {
          suggestion = rawSuggestion.slice(currentText.length).trim();
        }
        const sections = parseLiveSuggestionSections(suggestion)
          .map((section) => ({
            ...section,
            chips: section.chips.filter((chip) => {
              const chipKey = chip.key;
              return (
                !!chipKey &&
                !suggestedKeysRef.current.has(chipKey) &&
                !(chipKey.length >= 10 && currentTextKey.includes(chipKey)) &&
                !currentText.endsWith(chip.text)
              );
            }),
          }))
          .filter((section) => section.chips.length);

        if (!sections.length) {
          setLiveSuggestionSections([]);
          return;
        }

        const hasAnyChips = sections.some((section) => section.chips.length > 0);
        if (!hasAnyChips) {
          setLiveSuggestionSections([]);
          return;
        }

        setLiveSuggestionSections(sections);
      } catch (err: any) {
        if (requestId !== autoSuggestRequestIdRef.current) return;
        setLiveSuggestionSections([]);
        setLiveSuggestError(err?.message || tr('Live AI suggestion is currently unavailable.'));
      } finally {
        if (requestId === autoSuggestRequestIdRef.current) {
          setLiveSuggestLoading(false);
        }
      }
    }, 900);
  }

  async function runAiAutoCompleteAnalysis() {
    const currentHtml = sanitizeDirectionalMarkup(editorRef.current?.innerHTML || draftHtml);
    const currentText = htmlToText(currentHtml);
    if (!currentText.length) {
      setError(tr('Add some note content before running AI Auto Complete.'));
      return;
    }

    setAiBusy('analysis');
    setError(null);
    setBanner(null);
    try {
      const result = await fetchJson<NoteCopilotResponse>(`/patients/${patientId}/notes/ai/copilot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: draftTitle,
          noteDraft: currentText,
          language: copilotLanguage,
          retrievedGuidelines: [],
        }),
      });

      const diagnoses = result.suggestions.differential.slice(0, 5);
      const missingQuestions = result.suggestions.missing_data_prompts.slice(0, 8);
      const redFlags = result.suggestions.red_flags.slice(0, 4);
      const recommendedSteps = result.entities_to_store.orders_recommended.slice(0, 6);

      const html = `
        <h3>${tr('AI Clinical Summary')}</h3>
        <h4>${tr('Proposed Diagnoses')}</h4>
        <ul>${diagnoses.map((d) => `<li><strong>${escapeHtml(d.label)}</strong>: ${escapeHtml((d.why || []).slice(0, 2).join('; '))}</li>`).join('')}</ul>
        <h4>${tr('Missing Questions')}</h4>
        <ul>${missingQuestions.map((q) => `<li>${escapeHtml(q.category)}: ${escapeHtml(q.prompt)}</li>`).join('')}</ul>
        <h4>${tr('Red Flags')}</h4>
        <ul>${redFlags.map((r) => `<li><strong>${escapeHtml(r.title)}</strong> (${escapeHtml(r.severity)}): ${escapeHtml(r.recommended_action)}</li>`).join('')}</ul>
        <h4>${tr('Suggested Next Steps')}</h4>
        <ul>${recommendedSteps.map((s) => `<li>${escapeHtml(s.text)} - ${escapeHtml(s.rationale)}</li>`).join('')}</ul>
      `;

      insertHtmlAtCursor(`<hr/>${html}<p></p>`);
      setBanner(tr('AI analysis inserted. Please review and edit before signing.'));
    } catch (err: any) {
      setError(err.message || tr('AI Auto Complete failed.'));
    } finally {
      setAiBusy(null);
    }
  }

  async function runEchoAiTask(task: 'treatment-note' | 'note-template' | 'note-writing' | 'consultation-summary' | 'prescription-support' | 'mobile-echo', busy: NonNullable<typeof aiBusy>) {
    const currentHtml = sanitizeDirectionalMarkup(editorRef.current?.innerHTML || draftHtml);
    const currentText = htmlToText(currentHtml);
    if (!currentText.length && task !== 'note-template') {
      setError(tr('Add some note content before running Echo AI.'));
      return;
    }
    setAiBusy(busy);
    setError(null);
    setBanner(null);
    try {
      const result = await fetchJson<EchoAiResponse>('/agent/echo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task,
          title: draftTitle,
          sourceText: task === 'note-template' ? buildSoapTemplate(tr).replace(/<[^>]+>/g, ' ') : currentText,
          instruction: task === 'note-template' ? 'Create a structured clinical template for this note.' : undefined,
        }),
      });
      const checklist = result.checklist?.length
        ? `<ul>${result.checklist.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`
        : '';
      insertHtmlAtCursor(`<hr/><h3>${escapeHtml(result.title)}</h3>${result.contentHtml}${checklist}<p></p>`);
      setBanner(tr('Echo AI draft inserted. Review and edit before signing.'));
    } catch (err: any) {
      setError(err.message || tr('Echo AI failed.'));
    } finally {
      setAiBusy(null);
    }
  }

  async function captureSpeechDraft(task: 'voice-to-note' | 'conversation-transcription', busy: 'voice' | 'conversation') {
    setError(null);
    setBanner(null);
    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const fallbackTranscript = !SpeechRecognitionCtor
      ? window.prompt(tr('Paste or type the dictated transcript'))
      : null;

    const transcript = fallbackTranscript ?? await new Promise<string>((resolve, reject) => {
      const recognition = new SpeechRecognitionCtor();
      recognition.lang = navigator.language || 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.onresult = (event: any) => resolve(Array.from(event.results).map((result: any) => result[0]?.transcript || '').join(' '));
      recognition.onerror = () => reject(new Error(tr('Dictation failed.')));
      recognition.onend = () => undefined;
      recognition.start();
    }).catch((err: Error) => {
      setError(err.message);
      return '';
    });

    if (!transcript?.trim()) return;
    setAiBusy(busy);
    try {
      const result = await fetchJson<EchoAiResponse>('/agent/echo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task, sourceText: transcript }),
      });
      insertHtmlAtCursor(`<hr/><h3>${escapeHtml(result.title)}</h3>${result.contentHtml}<p></p>`);
      setBanner(task === 'voice-to-note' ? tr('Voice-to-note draft inserted.') : tr('Conversation summary inserted.'));
    } catch (err: any) {
      setError(err.message || tr('Echo AI dictation failed.'));
    } finally {
      setAiBusy(null);
    }
  }

  async function saveNote() {
    const currentHtml = sanitizeDirectionalMarkup(editorRef.current?.innerHTML || draftHtml);
    const plainText = htmlToText(currentHtml);
    if (!plainText.length) {
      setError(tr('Note content cannot be empty.'));
      return;
    }

    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const payload = {
        title: draftTitle.trim() || undefined,
        contentHtml: currentHtml,
        pinned: draftPinned,
        treatmentPlanId: draftTreatmentPlanId || null,
      };

      const path = isCreating ? `/patients/${patientId}/notes` : `/patients/${patientId}/notes/${selectedId}`;
      const method = isCreating ? 'POST' : 'PUT';
      const saved = await fetchJson<PatientNote>(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      setBanner(isCreating ? tr('Note created.') : tr('Note updated.'));
      setDirty(false);
      await loadNotes(saved.id);
    } catch (err: any) {
      setError(err.message || tr('Failed to save note.'));
    } finally {
      setSaving(false);
    }
  }

  async function deleteNote() {
    if (!selectedId || isCreating) return;
    if (!window.confirm(tr('Delete this note? This action can be audited and hidden from active views.'))) return;

    setDeleting(true);
    setError(null);
    setBanner(null);
    try {
      const res = await fetchApi(`/patients/${patientId}/notes/${selectedId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setBanner(tr('Note deleted.'));
      await loadNotes();
    } catch (err: any) {
      setError(err.message || tr('Failed to delete note.'));
    } finally {
      setDeleting(false);
    }
  }

  function insertSoapTemplate() {
    if (htmlToText(editorRef.current?.innerHTML || draftHtml).length > 0) {
      if (!window.confirm(tr('Replace current note content with SOAP template?'))) return;
    }
    const template = buildSoapTemplate(tr);
    if (editorRef.current) editorRef.current.innerHTML = template;
    setDraftHtml(template);
    setDirty(true);
    scheduleLiveAutocomplete(template);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Clinical Notes')}</p>
            <h3 className="text-lg font-semibold text-slate-900">{tr('Patient Notes Workspace')}</h3>
            <p className="text-xs text-slate-500">{tr('Rich note editor with live autocomplete and structured AI clinical completion.')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={createNewNote}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <FilePlus2 size={14} /> {tr('New Note')}
            </button>
            <button
              onClick={() => {
                const next = !autoSuggestEnabled;
                setAutoSuggestEnabled(next);
                if (!next) {
                  setLiveSuggestionSections([]);
                  setLiveSuggestLoading(false);
                  setLiveSuggestError(null);
                } else {
                  scheduleLiveAutocomplete(editorRef.current?.innerHTML || draftHtml);
                }
              }}
              className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium ${autoSuggestEnabled ? 'border-lime-300 bg-lime-100 text-lime-950 hover:bg-lime-200' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
            >
              {autoSuggestEnabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              {autoSuggestEnabled ? tr('Live AI Suggestion On') : tr('Live AI Suggestion Off')}
            </button>
            <button
              onClick={saveNote}
              disabled={saving || (!dirty && !isCreating)}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? tr('Saving...') : tr('Save')}
            </button>
            <button
              onClick={deleteNote}
              disabled={deleting || isCreating || !selectedId}
              className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {tr('Delete')}
            </button>
          </div>
        </div>
      </div>

      {banner && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{banner}</div>}
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tr('Search notes...')}
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded"
                dir="ltr"
                style={{ textAlign: 'left', direction: 'ltr', unicodeBidi: 'normal', writingMode: 'horizontal-tb' }}
              />
            </div>
            <label className="text-xs text-slate-600 inline-flex items-center gap-2">
              <input type="checkbox" checked={pinnedOnly} onChange={(e) => setPinnedOnly(e.target.checked)} />
              {tr('Pinned only')}
            </label>
          </div>
          <div className="max-h-[580px] overflow-auto divide-y divide-slate-100">
            {loading ? (
              <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> {tr('Loading notes...')}
              </div>
            ) : notes.length ? (
              notes.map((note) => (
                <button
                  key={note.id}
                  onClick={() => selectNote(note)}
                  className={`w-full text-left p-3 hover:bg-slate-50 ${selectedId === note.id && !isCreating ? 'bg-slate-50' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-slate-800 line-clamp-1">{note.title}</div>
                    {note.pinned && <Pin size={13} className="text-amber-600 mt-0.5" />}
                  </div>
                  {note.treatmentPlan ? <div className="mt-1 text-[11px] text-sky-700">{tr('Plan')}: {note.treatmentPlan.title}</div> : null}
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{note.contentText || tr('No content preview')}</div>
                  <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
                    <Clock3 size={11} /> {formatDateTime(note.updatedAt)} • {note.author.lastName}, {note.author.firstName}
                  </div>
                </button>
              ))
            ) : (
              <div className="p-4 text-sm text-slate-500">{tr('No notes found for this patient.')}</div>
            )}
          </div>
        </div>

        <div className="xl:col-span-9 rounded-xl border border-slate-200 bg-white">
          <div className="p-3 border-b border-slate-100 space-y-3">
            <input
              value={draftTitle}
              onChange={(e) => {
                setDraftTitle(e.target.value);
                setDirty(true);
              }}
              placeholder={tr('Note title')}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-medium"
              dir="ltr"
              style={{ textAlign: 'left', direction: 'ltr', unicodeBidi: 'normal', writingMode: 'horizontal-tb' }}
            />
            <label className="block space-y-1">
              <span className="text-xs uppercase tracking-wide text-slate-500">{tr('Linked Treatment Plan')}</span>
              <select
                value={draftTreatmentPlanId}
                onChange={(e) => {
                  setDraftTreatmentPlanId(e.target.value);
                  setDirty(true);
                }}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              >
                <option value="">{tr('No linked treatment plan')}</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1">
                <button onClick={() => runCommand('bold')} className="p-2 rounded border border-slate-300 hover:bg-slate-50" title={tr('Bold')}><Bold size={14} /></button>
                <button onClick={() => runCommand('italic')} className="p-2 rounded border border-slate-300 hover:bg-slate-50" title={tr('Italic')}><Italic size={14} /></button>
                <button onClick={() => runCommand('underline')} className="p-2 rounded border border-slate-300 hover:bg-slate-50" title={tr('Underline')}><Underline size={14} /></button>
                <button onClick={() => runCommand('insertUnorderedList')} className="p-2 rounded border border-slate-300 hover:bg-slate-50" title={tr('Bullet List')}><List size={14} /></button>
                <button onClick={() => runCommand('insertOrderedList')} className="p-2 rounded border border-slate-300 hover:bg-slate-50" title={tr('Numbered List')}><ListOrdered size={14} /></button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.currentTarget.value = '';
                    if (!file) return;
                    await insertImageFromFile(file);
                  }}
                />
                <button onClick={() => imageInputRef.current?.click()} className="p-2 rounded border border-slate-300 hover:bg-slate-50" title={tr('Insert Image')}><ImagePlus size={14} /></button>
                <button
                  onClick={() => {
                    const url = window.prompt(tr('Enter URL'));
                    if (url) runCommand('createLink', url);
                  }}
                  className="p-2 rounded border border-slate-300 hover:bg-slate-50"
                  title={tr('Insert Link')}
                >
                  <Link2 size={14} />
                </button>
                <button onClick={() => runCommand('removeFormat')} className="rounded border border-lime-200 px-2.5 py-2 text-xs text-lime-800 hover:bg-lime-50">{tr('Clear')}</button>
                <button onClick={insertSoapTemplate} className="rounded border border-lime-200 px-2.5 py-2 text-xs text-lime-800 hover:bg-lime-50">{tr('SOAP Template')}</button>
                <button onClick={() => void runEchoAiTask('treatment-note', 'treatment')} disabled={Boolean(aiBusy)} className="inline-flex items-center gap-1 rounded border border-sky-200 px-2.5 py-2 text-xs text-sky-800 hover:bg-sky-50 disabled:opacity-50">
                  {aiBusy === 'treatment' ? <Loader2 size={13} className="animate-spin" /> : <BrainCircuit size={13} />} {tr('Echo AI Treatment Notes')}
                </button>
                <button onClick={() => void runEchoAiTask('note-template', 'summary')} disabled={Boolean(aiBusy)} className="inline-flex items-center gap-1 rounded border border-sky-200 px-2.5 py-2 text-xs text-sky-800 hover:bg-sky-50 disabled:opacity-50">
                  <ClipboardCheck size={13} /> {tr('AI Note Templates')}
                </button>
                <button onClick={() => void captureSpeechDraft('voice-to-note', 'voice')} disabled={Boolean(aiBusy)} className="inline-flex items-center gap-1 rounded border border-sky-200 px-2.5 py-2 text-xs text-sky-800 hover:bg-sky-50 disabled:opacity-50">
                  {aiBusy === 'voice' ? <Loader2 size={13} className="animate-spin" /> : <Mic size={13} />} {tr('Voice-to-Note')}
                </button>
                <button onClick={() => void captureSpeechDraft('conversation-transcription', 'conversation')} disabled={Boolean(aiBusy)} className="inline-flex items-center gap-1 rounded border border-sky-200 px-2.5 py-2 text-xs text-sky-800 hover:bg-sky-50 disabled:opacity-50">
                  <MessageSquareText size={13} /> {tr('Patient Conversation Transcription')}
                </button>
                <button onClick={() => void runEchoAiTask('consultation-summary', 'summary')} disabled={Boolean(aiBusy)} className="inline-flex items-center gap-1 rounded border border-sky-200 px-2.5 py-2 text-xs text-sky-800 hover:bg-sky-50 disabled:opacity-50">
                  <BrainCircuit size={13} /> {tr('Consultation Summaries')}
                </button>
                <button onClick={() => void runEchoAiTask('prescription-support', 'prescription')} disabled={Boolean(aiBusy)} className="inline-flex items-center gap-1 rounded border border-amber-200 px-2.5 py-2 text-xs text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                  {aiBusy === 'prescription' ? <Loader2 size={13} className="animate-spin" /> : <Pill size={13} />} {tr('AI Prescription Support')}
                </button>
                <button onClick={() => void runEchoAiTask('mobile-echo', 'summary')} disabled={Boolean(aiBusy)} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <Mic size={13} /> {tr('Mobile Echo AI')}
                </button>
              </div>
              <label className="text-sm text-slate-600 inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draftPinned}
                  onChange={(e) => {
                    setDraftPinned(e.target.checked);
                    setDirty(true);
                  }}
                />
                {tr('Pin note')}
              </label>
            </div>
          </div>

          <div className="p-3 space-y-3">
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              dir="ltr"
              style={{ textAlign: 'left', direction: 'ltr', unicodeBidi: 'normal', writingMode: 'horizontal-tb' }}
              onPaste={async (e) => {
                const files = Array.from(e.clipboardData?.files || []);
                const image = files.find((file) => file.type.startsWith('image/'));
                if (!image) return;
                e.preventDefault();
                await insertImageFromFile(image);
              }}
              onInput={(e) => {
                const target = e.target as HTMLDivElement;
                const rawHtml = target.innerHTML;
                const sanitizedHtml = sanitizeDirectionalMarkup(rawHtml);
                if (sanitizedHtml !== rawHtml) {
                  target.innerHTML = sanitizedHtml;
                }
                setDraftHtml(sanitizedHtml);
                setDirty(true);
                scheduleLiveAutocomplete(sanitizedHtml);
              }}
              onKeyDown={(e) => {
                const firstChip = liveSuggestionSections
                  .filter((section) => section.section !== 'additionalQuestions')
                  .flatMap((section) => section.chips)[0];
                if (e.key === 'Tab' && firstChip) {
                  e.preventDefault();
                  insertLiveSuggestionChip(firstChip.text);
                }
              }}
              className="min-h-[360px] border border-slate-300 rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
            />

            {autoSuggestEnabled && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sky-900">{tr('Live AI suggestion')}</span>
                  {liveSuggestLoading && (
                    <span className="inline-flex items-center gap-1 text-sky-700">
                      <Loader2 size={12} className="animate-spin" /> {tr('Thinking...')}
                    </span>
                  )}
                </div>
                {liveSuggestionSections.length ? (
                  <>
                    <div className="space-y-3">
                      {liveSuggestionSections.map((section, index) => {
                        const title =
                          section.section === 'redFlags'
                              ? tr('Red Flags')
                              : section.section === 'differential'
                                ? tr('Differential')
                                : section.section === 'additionalQuestions'
                                  ? tr('Additional Questions')
                                  : tr('Next Documentation Line');
                        const titleClass =
                          section.section === 'redFlags'
                              ? 'text-rose-600'
                              : section.section === 'differential'
                                ? 'text-slate-600'
                                : section.section === 'additionalQuestions'
                                  ? 'text-amber-700'
                                  : 'text-lime-700';
                        const chipClass =
                          section.section === 'redFlags'
                              ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                              : section.section === 'differential'
                                ? 'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100'
                                : section.section === 'additionalQuestions'
                                  ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                                  : 'border-lime-200 bg-lime-50 text-lime-900 hover:bg-lime-100';

                        return (
                          <div key={`${section.section}-${index}`} className="space-y-2">
                            <p className={`text-xs font-semibold uppercase tracking-wide ${titleClass}`}>{title}</p>
                            {section.chips.length ? (
                              <div className="flex flex-wrap gap-2">
                                {section.chips.map((chip) => (
                                  section.section === 'additionalQuestions' ? (
                                    <span
                                      key={`${section.section}-${chip.key}`}
                                      className={`rounded-full border px-3 py-1 text-left text-xs ${chipClass}`}
                                    >
                                      {chip.text}
                                    </span>
                                  ) : (
                                    <button
                                      key={`${section.section}-${chip.key}`}
                                      onClick={() => insertLiveSuggestionChip(chip.text)}
                                      className={`rounded-full border px-3 py-1 text-left text-xs ${chipClass}`}
                                    >
                                      {chip.text}
                                    </button>
                                  )
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-sky-700">{tr('Tip: press Tab to accept')}</div>
                  </>
                ) : liveSuggestError ? (
                  <div className="text-rose-600">{liveSuggestError}</div>
                ) : (
                  <div className="text-sky-700">{tr('No suggestion yet. Add a few symptoms or findings.')}</div>
                )}
              </div>
            )}
          </div>

          <div className="px-3 py-2 border-t border-slate-100 text-xs text-slate-500 flex flex-wrap gap-3">
            <span>{tr('Created')}: {formatDateTime(selectedNote?.createdAt)}</span>
            <span>{tr('Last updated')}: {formatDateTime(selectedNote?.updatedAt)}</span>
            <span>{tr('Author')}: {selectedNote ? `${selectedNote.author.firstName} ${selectedNote.author.lastName}` : tr('Current user')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
